import {
    Checkbox,
    PrimaryButton,
    ProgressIndicator,
    Stack,
    TextField,
} from '@fluentui/react';
import {
    PackageManager,
    PackageManagerInstallOptions,
} from '@yume-chan/android-bin';
import { WrapConsumableStream, WritableStream } from '@yume-chan/stream-extra';
import https from 'https';
import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { NextPage } from 'next';
import Head from 'next/head';
import { GLOBAL_STATE } from '../state';
import {
    ProgressStream,
    RouteStackProps,
    createFileStream,
    pickFile,
} from '../utils';

enum Stage {
    Uploading,

    Installing,

    Completed,
}

interface Progress {
    filename: string;

    stage: Stage;

    uploadedSize: number;

    totalSize: number;

    value: number | undefined;
}

class InstallPageState {
    installing = false;
    userInfo: string = '';
    progress: Progress | undefined = undefined;

    log: string = '';

    options: Partial<PackageManagerInstallOptions> = {
        bypassLowTargetSdkBlock: false,
    };

    apkUrl =
        'https://www.baibaomen.com/yiguan.202305051.apk';

    activeLog: string = '';

    constructor() {
        makeAutoObservable(this, {
            progress: observable.ref,
            install: false,
            options: observable.deep,
        });
    }

    upload = async () => {
        const file = await pickFile({ accept: '.apk' });
        if (!file) {
            return;
        }
        await this.install(file);
    };

    download = async () => {
        if (this.apkUrl.slice(-4) !== '.apk') {
            alert('Please enter the correct APK installation address');
            return;
        }
        const apkBytes: Uint8Array[] = [];
        const fileName = this.apkUrl.slice(this.apkUrl.lastIndexOf('/') + 1);

        https
            .get(this.apkUrl, (response) => {
                response
                    .on('data', (d: Uint8Array) => {
                        apkBytes.push(d);
                    })
                    .on('end', async () => {
                        const apkFile = new File(apkBytes, fileName, {
                            type: 'application/vnd.android.package-archive',
                        });
                        await this.install(apkFile);
                    });
            })
            .on('error', (error) => {
                console.log(error);
                alert('Failed to obtain apk file');
            });
    };

    install = async (file: File) => {
        runInAction(() => {
            this.installing = true;
            this.progress = {
                filename: file.name,
                stage: Stage.Uploading,
                uploadedSize: 0,
                totalSize: file.size,
                value: 0,
            };
            this.log = '';
        });

        const pm = new PackageManager(GLOBAL_STATE.device!);
        const start = Date.now();
        const log = await pm.installStream(
            file.size,
            createFileStream(file)
                .pipeThrough(new WrapConsumableStream())
                .pipeThrough(
                    new ProgressStream(
                        action((uploaded) => {
                            if (uploaded !== file.size) {
                                this.progress = {
                                    filename: file.name,
                                    stage: Stage.Uploading,
                                    uploadedSize: uploaded,
                                    totalSize: file.size,
                                    value: (uploaded / file.size) * 0.8,
                                };
                            } else {
                                this.progress = {
                                    filename: file.name,
                                    stage: Stage.Installing,
                                    uploadedSize: uploaded,
                                    totalSize: file.size,
                                    value: 0.8,
                                };
                            }
                        })
                    )
                )
        );

        const elapsed = Date.now() - start;
        await log.pipeTo(
            new WritableStream({
                write: action((chunk) => {
                    this.log += chunk;
                }),
            })
        );

        const transferRate = (
            file.size /
            (elapsed / 1000) /
            1024 /
            1024
        ).toFixed(2);
        this.log += `Install finished in ${elapsed}ms at ${transferRate}MB/s`;

        runInAction(() => {
            this.progress = {
                filename: file.name,
                stage: Stage.Completed,
                uploadedSize: file.size,
                totalSize: file.size,
                value: 1,
            };
            this.installing = false;
        });
    };

    uint8ToUtf8 = (value: Uint8Array) => {
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(value);
    };

    findApkInstallPathShell = async (apkName: string) => {
        if (GLOBAL_STATE.device) {
            const result = await GLOBAL_STATE.device.subprocess.shell(
                `pm path ${apkName}`
            );

            const stdout = await result.stdout.getReader().read();
            if (stdout.value) {
                return this.uint8ToUtf8(stdout.value);
            } else {
                this.activeLog = 'APK installation directory not found';
            }
        }
    };

    activeDeviceShell = async () => {
        this.activeLog = '';
        if (GLOBAL_STATE.device) {
            const path = await this.findApkInstallPathShell('com.yiguan.hy');

            console.log('path', path);

            if (path) {
                const _path = path
                    .replace('package:', '')
                    .replace(/[\r\n]/gm, '');

                const activeShell =
                    "app_process -Djava.class.path='" +
                    _path +
                    "' /system/bin com.yiguan.hy.Activator \"" +
                    this.userInfo + "\"";

                console.log('activeShell', activeShell);

                const result = await GLOBAL_STATE.device.subprocess.shell(
                    activeShell
                );

                const reader = result.stdout.getReader();
                let self = this;
                reader.read().then(function processText({ done, value }): any {
                    if (done) {
                        if (value) self.activeLog += self.uint8ToUtf8(value);
                        return;
                    }

                    if (value) self.activeLog += self.uint8ToUtf8(value);
                    return reader.read().then(processText);
                });
            }
        }
    };
}

const state = new InstallPageState();

const Install: NextPage = () => {
    return (
        <Stack {...RouteStackProps}>
            <Head>
                <title>Install APK - Tango</title>
            </Head>
            <h2>Local install APK</h2>
            <Stack horizontal>
                <Checkbox
                    label='--bypass-low-target-sdk-block (Android 14)'
                    checked={state.options.bypassLowTargetSdkBlock}
                    onChange={(_, checked) => {
                        if (checked === undefined) {
                            return;
                        }
                        runInAction(() => {
                            state.options.bypassLowTargetSdkBlock = checked;
                        });
                    }}
                />
            </Stack>

            <Stack horizontal>
                <PrimaryButton
                    disabled={!GLOBAL_STATE.device || state.installing}
                    text='Browse APK'
                    onClick={state.upload}
                />
            </Stack>
            <h2>Remote install APK</h2>
            <Stack horizontal>
                <TextField
                    style={{ width: '80vw' }}
                    placeholder='Input APK download address'
                    value={state.apkUrl}
                    onChange={(_, value) => {
                        state.apkUrl = value || '';
                    }}
                />
            </Stack>

            <Stack horizontal>
                <PrimaryButton
                    disabled={!GLOBAL_STATE.device || state.installing}
                    text='Install Remote APK'
                    onClick={state.download}
                />
            </Stack>

            <h2>Send active command</h2>
            <Stack>
                <Stack horizontal>
                            <TextField
                    style={{ width: '40vw' }}
                    placeholder='Input user info'
                    value={state.userInfo} required={true} minLength={20}
                    onChange={(_, value) => {
                        state.userInfo = value || '';
                    }}
                />
                    <PrimaryButton
                        disabled={!GLOBAL_STATE.device || state.installing}
                        text='Send activation command'
                        onClick={state.activeDeviceShell}
                    />
                </Stack>
            </Stack>

            {state.activeLog && <pre>{state.activeLog}</pre>}
            {state.progress && (
                <ProgressIndicator
                    styles={{ root: { width: 300 } }}
                    label={state.progress.filename}
                    percentComplete={state.progress.value}
                    description={Stage[state.progress.stage]}
                />
            )}

            {state.log && <pre>{state.log}</pre>}
        </Stack>
    );
};

export default observer(Install);
